// @ts-check
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminSiteSettingsService from '../services/adminSiteSettingsService';
import FileUpload from './FileUpload';

// Mirrors the icon keys in frontend-next/src/constants/trustPillarIcons.js — keep the
// two lists in sync by hand when adding/removing an option (admin-frontend can't import
// across packages, so there's no single shared source of truth here).
const ICON_OPTIONS = [
  'Leaf',
  'Feather',
  'Flower2',
  'Cloud',
  'Wind',
  'HandHeart',
  'Sparkles',
  'Heart',
  'ShieldCheck',
  'Recycle',
  'Sun',
  'Droplet',
  'Package',
  'Truck',
  'Award',
  'Gem',
  'Sprout',
  'Scissors',
  'DressFormIcon',
  'GlobeShieldIcon',
];

// Default catalog — mirrors frontend-next/src/constants/trustPillars.js's
// DEFAULT_STATEMENT/DEFAULT_PILLARS. Used only when no admin config has been saved yet;
// keep the copy in sync with that file if it changes.
const DEFAULT_STATEMENT = 'Quiet luxury, made the slow way — natural fibres, small batches, hand-finished details.';
const DEFAULT_PILLARS = [
  {
    id: 'linen-silk',
    icon: 'Feather',
    imageUrl: null,
    label: 'Linen-Silk',
    tag: 'Linen-Silk',
    short: 'Linen-silk',
    description:
      'A refined blend of linen and silk that is remarkably light and airy. It breathes in the heat, drapes with a fluid softness, and carries a subtle natural sheen that only grows more beautiful with wear.',
    matchKeywords: ['linen', 'silk'],
  },
  {
    id: 'vegan-silk',
    icon: 'Flower2',
    imageUrl: null,
    label: 'Vegan Silk',
    tag: 'Vegan Silk',
    short: 'Vegan silk',
    description:
      'Silk woven entirely from plants, never from silkworms — all the softness and sheen of silk with none of the cruelty. Plant-based silk is slower and more costly to make, which is part of why these pieces are priced the way they are.',
    matchKeywords: ['silk'],
  },
  {
    id: 'egyptian-cotton',
    icon: 'Cloud',
    imageUrl: null,
    label: 'Egyptian Cotton',
    tag: 'Egyptian Cotton',
    short: 'Egyptian cotton',
    description:
      'Not just cotton — Egyptian cotton, prized for its extra-long fibres that make it exceptionally soft, strong, and breathable. It feels gentle against the skin and only improves with age. The finest cotton in the world costs more for good reason.',
    matchKeywords: ['cotton'],
  },
  {
    id: 'premium-linen',
    icon: 'Wind',
    imageUrl: null,
    label: 'Premium Linen',
    tag: 'Premium Linen',
    short: 'Premium linen',
    description:
      'Spun from pure linen, one of the oldest and most breathable natural fibres there is. It keeps you cool, softens beautifully with every wash, and is fully biodegradable. Fine linen is labour-intensive to weave — part of what you feel in the price.',
    matchKeywords: ['linen'],
  },
  {
    id: 'natural-fibres',
    icon: 'Leaf',
    imageUrl: null,
    label: '100% Natural Fibres',
    tag: 'Natural Fibres',
    short: 'Natural fibres',
    description:
      'Every thread is a natural, breathable fibre — never polyester or synthetic blends. Natural fibres cost more and are harder to source, but they breathe with your body, age beautifully, and return to the earth at the end of their life.',
    matchKeywords: [],
  },
  {
    id: 'perfect-fit',
    icon: 'DressFormIcon',
    imageUrl: null,
    label: 'Made to Move',
    tag: 'Made to Move',
    short: 'Made to move',
    description:
      "A perfect fit disappears — you simply stop thinking about what you're wearing. Every KAAY piece is cut to sit exactly where it should, with proportions that flatter without effort and seams that follow the body rather than resist it.",
    matchKeywords: [],
  },
  {
    id: 'ethically-made',
    icon: 'HandHeart',
    imageUrl: null,
    label: 'Ethically Made',
    tag: 'Hand-Finished',
    short: 'Ethically made',
    description:
      'Each piece is made slowly and by hand, with respect for the people and the craft behind it. Nothing here is mass-produced — and that intention is woven into every seam.',
    matchKeywords: [],
  },
  {
    id: 'worldwide-delivery',
    icon: 'GlobeShieldIcon',
    imageUrl: null,
    label: 'Worldwide Delivery, Duties Paid',
    tag: 'Duties Paid Worldwide',
    short: 'Worldwide, duties paid',
    description:
      'We ship to over 150 countries, and the price you see is the price you pay — all import duties and taxes are already included and paid by us, so there is nothing to settle at customs and nothing due on delivery. Every order is tracked from our door to yours.',
    matchKeywords: [],
  },
];

const blankPillar = () => ({
  id: `pillar-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
  icon: 'Leaf',
  imageUrl: null,
  label: '',
  tag: '',
  short: '',
  description: '',
  matchKeywords: [],
});

const TrustPillarsManager = () => {
  const toast = useToast();
  const [statement, setStatement] = useState(DEFAULT_STATEMENT);
  const [pillars, setPillars] = useState(DEFAULT_PILLARS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const setting = await adminSiteSettingsService.getSetting('trust_pillars_config');
        const parsed = JSON.parse(setting.value);
        setStatement(parsed.statement || DEFAULT_STATEMENT);
        setPillars(Array.isArray(parsed.pillars) && parsed.pillars.length > 0 ? parsed.pillars : DEFAULT_PILLARS);
      } catch {
        // Not configured yet — start from the current defaults, matching how
        // ShippingTab/PaymentTab seed their forms on first use.
        setStatement(DEFAULT_STATEMENT);
        setPillars(DEFAULT_PILLARS);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSiteSettingsService.bulkUpdate({
        trust_pillars_config: JSON.stringify({ statement, pillars }),
      });
      toast.success('Trust pillars saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save trust pillars');
    } finally {
      setSaving(false);
    }
  };

  const updatePillar = (index, field, value) => {
    setPillars((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const updateKeywords = (index, text) => {
    const keywords = text
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    updatePillar(index, 'matchKeywords', keywords);
  };

  const movePillar = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= pillars.length) return;
    const updated = [...pillars];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setPillars(updated);
  };

  const removePillar = (index) => {
    setPillars((prev) => prev.filter((_, i) => i !== index));
  };

  const addPillar = () => {
    setPillars((prev) => [...prev, blankPillar()]);
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading trust pillars...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-1">Trust Statement</label>
        <p className="text-xs text-muted-foreground/70 mb-2">Shown above the trust marks on the product page.</p>
        <textarea
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
        />
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h4 className="text-sm font-medium">Pillars</h4>
            <p className="text-xs text-muted-foreground/70">
              A pillar with match keywords auto-attaches to a product whose fabric composition contains those words
              (e.g. "linen, silk"). Leave keywords empty for a universal pillar shown everywhere — footer, homepage, and
              every product page.
            </p>
          </div>
          <button
            type="button"
            onClick={addPillar}
            className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs hover:bg-primary/90 transition flex-shrink-0"
          >
            <Plus size={14} />
            Add Pillar
          </button>
        </div>

        <div className="space-y-3">
          {pillars.map((pillar, index) => (
            <div key={pillar.id} className="border border-border rounded-lg p-4 bg-card space-y-3">
              <div className="flex items-start justify-between gap-2">
                <span
                  className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    pillar.matchKeywords?.length
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {pillar.matchKeywords?.length
                    ? `Matches: ${pillar.matchKeywords.join(', ')}`
                    : 'Universal (always shown)'}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => movePillar(index, -1)}
                    disabled={index === 0}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => movePillar(index, 1)}
                    disabled={index === pillars.length - 1}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePillar(index)}
                    className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                    title="Delete pillar"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Label</label>
                  <input
                    type="text"
                    value={pillar.label}
                    onChange={(e) => updatePillar(index, 'label', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Tag (shown under icon)</label>
                  <input
                    type="text"
                    value={pillar.tag}
                    onChange={(e) => updatePillar(index, 'tag', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Short (compact badge text)</label>
                  <input
                    type="text"
                    value={pillar.short}
                    onChange={(e) => updatePillar(index, 'short', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Match Keywords (comma-separated)</label>
                  <input
                    type="text"
                    value={(pillar.matchKeywords || []).join(', ')}
                    onChange={(e) => updateKeywords(index, e.target.value)}
                    placeholder="e.g. linen, silk — leave blank for universal"
                    className="w-full px-2.5 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Description (popup text)</label>
                <textarea
                  value={pillar.description}
                  onChange={(e) => updatePillar(index, 'description', e.target.value)}
                  rows={2}
                  className="w-full px-2.5 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                <div>
                  <label className="block text-xs font-medium mb-1">Icon (used when no image is uploaded)</label>
                  <select
                    value={pillar.icon}
                    onChange={(e) => updatePillar(index, 'icon', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  >
                    {ICON_OPTIONS.map((name) => (
                      <option key={name} value={name}>
                        {name === 'DressFormIcon'
                          ? 'Dress Form (custom)'
                          : name === 'GlobeShieldIcon'
                            ? 'Globe Shield (custom)'
                            : name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Image (overrides icon)</label>
                  {pillar.imageUrl ? (
                    <div className="flex items-center gap-2 border border-border rounded-lg p-2">
                      <img src={pillar.imageUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
                      <button
                        type="button"
                        onClick={() => updatePillar(index, 'imageUrl', null)}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <FileUpload
                      uploadType="trust-pillar-image"
                      accept="image/*"
                      multiple={false}
                      maxFiles={1}
                      onUploadComplete={(files) => {
                        const uploaded = (files || []).find((f) => f.uploaded && f.url);
                        if (uploaded) updatePillar(index, 'imageUrl', uploaded.url);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
          {pillars.length === 0 && (
            <p className="text-center text-muted-foreground/70 py-4 text-sm">
              No pillars yet — click "Add Pillar" to start.
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50 rounded-lg text-sm"
        >
          {saving ? 'Saving...' : 'Save Trust Pillars'}
        </button>
      </div>
    </div>
  );
};

export default TrustPillarsManager;
